import React, { useRef, useEffect } from 'react';
import './index.less';
import {
  BoldIcon,
  ItalicIcon,
  RichTextToolbarIcon,
  SortIcon
} from '@/components/common/icon';
import UploadImages from './UploadImages';
import UploadVideos from './UploadVideos';
import { useWebModalStore } from '@/hooks/useWebModalStore';

/**
 * 自定义 富文本编辑器
 * @param orderId 订单ID
 * @param value 富文本内容
 * @param saveData 保存富文本内容
 * @param placeholder 空内容时的占位提示
 * 支持加粗 倾斜 排序 插入图片 插入视频
 * @returns
 */

const isEditorContentEmpty = (element: HTMLElement): boolean => {
  if (element.querySelector('img, video')) {
    return false;
  }

  const text = (element.textContent || '').replace(/\u00A0/g, ' ').trim();
  return text.length === 0;
};

const SimpleRichText = ({
  orderId,
  value,
  saveData,
  placeholder = '',
  variant = 'default',
  ariaLabel,
  error = false
}: {
  orderId: string;
  value: string;
  saveData: (value: string) => void;
  placeholder?: string;
  variant?: 'default' | 'creator-delivery';
  ariaLabel?: string;
  error?: boolean;
}) => {
  const { setShowModalShowImage } = useWebModalStore();
  const editorRef = useRef<HTMLDivElement>(null);
  const insertedImagesRef = useRef<Set<string>>(new Set());
  const insertedVideosRef = useRef<Set<string>>(new Set());
  const savedRangeRef = useRef<Range | null>(null);
  const isInternalUpdateRef = useRef(false);

  const updateEmptyState = () => {
    if (!editorRef.current) return;

    editorRef.current.classList.toggle(
      'is-empty',
      isEditorContentEmpty(editorRef.current)
    );
  };

  const configureVideoElement = (video: HTMLVideoElement) => {
    video.controls = true;
    video.preload = 'metadata';
    video.muted = true;
    video.defaultMuted = true;
    video.disablePictureInPicture = true;
    video.disableRemotePlayback = true;
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    video.setAttribute('contenteditable', 'false');
    video.setAttribute(
      'controlsList',
      'nodownload noplaybackrate noremoteplayback'
    );
  };

  // 初始化内容
  useEffect(() => {
    if (!editorRef.current) return;

    // 如果是由内部更新触发的，跳过
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }

    // 标准化比较：去除空白字符和浏览器自动添加的标签
    const currentContent = editorRef.current.innerHTML.trim();
    const newContent = (value || '').trim();

    // 如果内容不同，更新编辑器
    if (currentContent !== newContent) {
      editorRef.current.innerHTML = value || '';

      // 提取已存在的图片 URL，添加到已插入集合中
      const images = editorRef.current.querySelectorAll('img');
      insertedImagesRef.current.clear(); // 清空旧的集合
      images.forEach((img) => {
        const src = img.getAttribute('src');
        if (src) {
          insertedImagesRef.current.add(src);
        }
      });

      // 提取已存在的视频 URL，添加到已插入集合中
      const videos = editorRef.current.querySelectorAll('video');
      insertedVideosRef.current.clear(); // 清空旧的集合
      videos.forEach((video) => {
        configureVideoElement(video);
        const src = video.getAttribute('src');
        if (src) {
          insertedVideosRef.current.add(src);
        }
      });

      updateEmptyState();
    }
  }, [value]);

  const execCommand = (command: string, value: string = '') => {
    if (!editorRef.current) return;

    editorRef.current.focus();
    document.execCommand(command, false, value);
    isInternalUpdateRef.current = true;
    saveData(editorRef.current.innerHTML);
    updateEmptyState();
  };

  // 保存光标位置
  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      // 检查选区是否在编辑器内
      if (
        editorRef.current &&
        editorRef.current.contains(range.commonAncestorContainer)
      ) {
        savedRangeRef.current = range.cloneRange();
      } else {
        savedRangeRef.current = null;
      }
    } else {
      savedRangeRef.current = null;
    }
  };

  // 恢复光标位置
  const restoreSelection = () => {
    const selection = window.getSelection();
    if (!selection) return;

    if (savedRangeRef.current) {
      // 检查保存的范围是否仍然有效
      try {
        selection.removeAllRanges();
        selection.addRange(savedRangeRef.current);
        return savedRangeRef.current;
      } catch (e) {
        // 如果范围无效，返回 null
        savedRangeRef.current = null;
      }
    }

    // 如果没有保存的范围或范围无效，创建新范围
    if (!editorRef.current) return null;

    const range = document.createRange();
    range.selectNodeContents(editorRef.current);
    range.collapse(false); // 移动到末尾
    selection.removeAllRanges();
    selection.addRange(range);
    return range;
  };

  const insertMediaAtCaret = (urlList: string[], type: 'image' | 'video') => {
    if (!editorRef.current) return;

    // 确保编辑器有焦点
    editorRef.current.focus();

    // 恢复或获取光标位置
    let range = restoreSelection();
    if (!range) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        range = selection.getRangeAt(0);
        // 确保范围在编辑器内
        if (!editorRef.current.contains(range.commonAncestorContainer)) {
          range = document.createRange();
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
        }
      } else {
        range = document.createRange();
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
      }
    }

    const insertedSet =
      type === 'image' ? insertedImagesRef.current : insertedVideosRef.current;

    // 找出新增的媒体（不在已插入集合中的）
    const newUrls = urlList.filter((url) => !insertedSet.has(url));

    // 插入新增的媒体
    newUrls.forEach((url) => {
      let node: HTMLElement;

      if (type === 'image') {
        const img = document.createElement('img');
        img.src = url;
        img.setAttribute(
          'style',
          'max-width: 200px; max-height: 100px; object-fit: contain; display: inline-block; margin: 8px 8px 8px 0; vertical-align: top;'
        );
        node = img;
      } else {
        const video = document.createElement('video');
        video.src = url;
        video.setAttribute(
          'style',
          'max-width: 200px; max-height: 100px; object-fit: contain; display: inline-block; margin: 8px 8px 8px 0; vertical-align: top;'
        );
        configureVideoElement(video);
        node = video;
      }

      // 插入节点
      range.insertNode(node);

      // 在节点后插入一个空格，方便后续输入
      const textNode = document.createTextNode('\u00A0'); // 非断行空格
      range.setStartAfter(node);
      range.collapse(false);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.collapse(true);

      // 标记为已插入
      insertedSet.add(url);
    });

    // 更新选择范围
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }

    // 更新编辑器内容
    isInternalUpdateRef.current = true;
    saveData(editorRef.current.innerHTML);
    updateEmptyState();
  };

  // 处理上传完成的图片列表
  const handleImageListChange = (imageList: string[]) => {
    insertMediaAtCaret(imageList, 'image');
  };

  // 处理上传完成的视频列表
  const handleVideoListChange = (videoList: string[]) => {
    insertMediaAtCaret(videoList, 'video');
  };

  const handleEditorClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target;
    if (target instanceof HTMLImageElement) {
      e.preventDefault();
      e.stopPropagation();
      setShowModalShowImage(true, target.currentSrc || target.src);
      return;
    }
    saveSelection();
  };

  return (
    <div
      className={`simple-rich-text ${
        variant === 'creator-delivery' ? 'creator-delivery-rich-text' : ''
      }${error ? ' simple-rich-text-error' : ''}`}
    >
      {/* 工具栏 */}
      <div className="toolbar">
        <div className="toolbar-left">
          <button
            type="button"
            aria-label="Bold"
            onClick={(e) => {
              e.preventDefault();
              execCommand('bold');
            }}
          >
            {variant === 'creator-delivery' ? (
              <RichTextToolbarIcon name="bold" />
            ) : (
              <BoldIcon className="h-[18px] w-[18px] text-[#666F8D]" />
            )}
          </button>
          <button
            type="button"
            aria-label="Italic"
            onClick={(e) => {
              e.preventDefault();
              execCommand('italic');
            }}
          >
            {variant === 'creator-delivery' ? (
              <RichTextToolbarIcon name="italic" />
            ) : (
              <ItalicIcon className="h-4 w-4 text-[#666F8D]" />
            )}
          </button>
          <button
            type="button"
            aria-label="Ordered list"
            onClick={(e) => {
              e.preventDefault();
              execCommand('insertOrderedList');
            }}
          >
            {variant === 'creator-delivery' ? (
              <RichTextToolbarIcon name="list" />
            ) : (
              <SortIcon className="h-4 w-4 text-[#666F8D]" />
            )}
          </button>
        </div>
        <div
          className={
            variant === 'creator-delivery'
              ? 'flex items-center gap-[12px]'
              : 'flex gap-4 pr-1'
          }
        >
          {variant === 'creator-delivery' ? (
            <>
              <UploadVideos
                onChange={handleVideoListChange}
                orderId={orderId}
                icon={<RichTextToolbarIcon name="paperclip" />}
              />
              <UploadImages
                onChange={handleImageListChange}
                orderId={orderId}
                icon={<RichTextToolbarIcon name="photo" />}
              />
            </>
          ) : (
            <>
              <UploadImages
                onChange={handleImageListChange}
                orderId={orderId}
              />
              <UploadVideos
                onChange={handleVideoListChange}
                orderId={orderId}
              />
            </>
          )}
        </div>
      </div>

      {/* 可编辑区域 */}
      <div
        ref={editorRef}
        contentEditable
        role="textbox"
        aria-label={ariaLabel}
        aria-multiline="true"
        aria-invalid={error || undefined}
        className={`editor${!(value || '').trim() ? ' is-empty' : ''}`}
        data-placeholder={placeholder}
        onInput={(e) => {
          isInternalUpdateRef.current = true;
          saveData(e.currentTarget.innerHTML);
          updateEmptyState();
        }}
        onMouseUp={saveSelection}
        onKeyUp={saveSelection}
        onClick={handleEditorClick}
        suppressContentEditableWarning={true}
      />
    </div>
  );
};

export default SimpleRichText;
